import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  Hammer,
  PenLine,
  Send,
  Mail,
  Phone,
  ShieldHalf,
  Building2,
  MessagesSquare,
  Clock3,
  BellRing,
  CircleHelp,
  CalendarDays,
  Newspaper,
  ArrowUpRight,
  ArrowDown,
  CircleCheck,
  LoaderCircle,
} from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { PageHero } from "@/components/blocks/PageHero";
import { SectionHeading } from "@/components/blocks/SectionHeading";
import { CTABand } from "@/components/blocks/CTABand";
import { StatusPhases } from "@/components/blocks/StatusPhases";
import {
  FIELD,
  LABEL,
  ChipGroup,
  Honeypot,
  FormError,
} from "@/components/blocks/FormParts";
import {
  API_CONTACT,
  postJson,
  useSpamGuard,
  formErrorMessage,
  revealElement,
} from "@/lib/forms";
import { Reveal } from "@/components/Reveal";
import {
  CONTACT_EMAIL,
  CONTACT_PHONE,
  CONTACT_PHONE_HREF,
  ROUTE_DEMO,
  ROUTE_FAQ,
  ROUTE_WAITLIST,
} from "@/lib/brand";
import { FOUNDER_MONTHS, FOUNDER_UNTIL } from "@/lib/pricing";
import { STATUS_LAUNCH, STATUS_UPDATED } from "@/lib/status";

const SIDEBAR_ITEMS = [
  { id: "top", label: "Start", icon: Home },
  { id: "stand", label: "Stand", icon: Hammer },
  { id: "anliegen", label: "Anliegen", icon: MessagesSquare },
  { id: "formular", label: "Nachricht", icon: PenLine },
  { id: "cta", label: "Demo", icon: Send },
];

const ROLES = [
  {
    value: "verein",
    label: "Verein",
    icon: ShieldHalf,
    org: "TSV Musterstadt",
    mail: "name@verein.de",
  },
  {
    value: "unternehmen",
    label: "Unternehmen",
    icon: Building2,
    org: "Muster GmbH",
    mail: "name@unternehmen.de",
  },
  {
    value: "sonstiges",
    label: "Sonstiges",
    icon: MessagesSquare,
    org: "Verband, Redaktion, Agentur",
    mail: "name@beispiel.de",
  },
];

/** Anliegen im Formular. Für die Warteliste gibt es eine eigene Seite. */
const TOPICS = [
  {
    value: "frage",
    label: "Frage",
    icon: CircleHelp,
    placeholder: "Was möchtet ihr wissen?",
  },
  {
    value: "kennenlernen",
    label: "Kennenlernen",
    icon: CalendarDays,
    placeholder:
      "Worum soll es gehen, und wann passt es euch? Lieber am Bildschirm oder am Telefon?",
  },
  {
    value: "presse",
    label: "Presse und Partner",
    icon: Newspaper,
    placeholder: "Worum geht es, und bis wann braucht ihr eine Antwort?",
  },
];

/** Kacheln „Worüber ihr uns schreiben könnt“. Die Warteliste steht oben im Hinweis. */
const CONCERNS = [
  {
    topic: "frage",
    icon: CircleHelp,
    title: "Ihr habt Fragen",
    text: "Zu Preisen, zur Messung, zum Datenschutz oder zum Start. Vieles beantworten schon die häufigen Fragen, alles andere wir persönlich.",
    action: "Frage stellen",
    link: { label: "Häufige Fragen", to: ROUTE_FAQ },
  },
  {
    topic: "kennenlernen",
    icon: CalendarDays,
    title: "Ihr wollt MatchGate kennenlernen",
    text: "Wir stellen euch MatchGate am Bildschirm oder am Telefon vor und gehen euren Fall konkret durch: ob ihr als Verein Partner sucht oder als Unternehmen Vereine fördern wollt.",
    action: "Termin anfragen",
  },
  {
    topic: "presse",
    icon: Newspaper,
    title: "Ihr wollt berichten oder kooperieren",
    text: "Verbände, Medien und mögliche Partner, die über MatchGate berichten oder es gemeinsam mit uns voranbringen wollen.",
    action: "Anfrage senden",
  },
];

const BTN_PRIMARY =
  "group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white bg-gradient-to-r from-brand-orange to-brand-blue shadow-[0_0_35px_-6px_rgba(255,107,0,0.7)] hover:shadow-[0_0_45px_-4px_rgba(0,122,255,0.8)] hover:scale-[1.03] transition-all duration-300";
const BTN_SECONDARY =
  "group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white/80 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] hover:text-white transition-all duration-300";

const TAPE_ITEMS = ["Im Aufbau", `Start ${STATUS_LAUNCH.in}`, "Warteliste offen"];
const TAPE_STRIPES = {
  backgroundImage:
    "repeating-linear-gradient(-45deg, #02040A 0 7px, transparent 7px 14px)",
};

/**
 * Schräges Band über die volle Breite, angelehnt an ein Absperrband.
 * `front`: gefüllt im Markenverlauf und vor der Karte, sonst dunkel dahinter.
 * Das Band ist breiter als der Bildschirm, die Farbstopps liegen deshalb im
 * sichtbaren Mittelteil.
 */
const Tape = ({ front = false, className = "" }) => {
  const items = Array.from({ length: 6 }, () => TAPE_ITEMS).flat();
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute left-1/2 w-[160vw] -translate-x-1/2 -translate-y-1/2 ${className}`}
    >
      <div
        className={`relative overflow-hidden ${
          front
            ? "bg-[linear-gradient(90deg,#FF6B00_25%,#FF8A3D_42%,#4A9BFF_66%,#007AFF_78%)] shadow-[0_18px_50px_-12px_rgba(0,0,0,0.9)]"
            : "bg-navy-800 border-y border-white/10"
        }`}
      >
        {front && (
          <>
            <div className="absolute inset-x-0 top-0 h-1.5 opacity-70" style={TAPE_STRIPES} />
            <div className="absolute inset-x-0 bottom-0 h-1.5 opacity-70" style={TAPE_STRIPES} />
          </>
        )}
        <div
          className={`flex w-max animate-marquee ${front ? "" : "[animation-direction:reverse]"}`}
          style={{ animationDuration: "70s" }}
        >
          {[...items, ...items].map((text, i) => (
            <span key={i} className="flex items-center gap-6 pl-6 py-3.5 md:py-4 shrink-0">
              <span
                className={`font-display font-black uppercase tracking-[0.2em] text-sm md:text-base whitespace-nowrap ${
                  front ? "text-navy-950" : "text-white/30"
                }`}
              >
                {text}
              </span>
              <span
                className={`w-2 h-2 rotate-45 ${front ? "bg-navy-950/70" : "bg-white/20"}`}
              />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Großer Hinweis direkt unter dem Kopf: Stand der Plattform und Weg zur
 * Warteliste. Bricht bewusst mit dem übrigen Design (Bänder, Verlaufsrand,
 * gefüllte Karte), damit er nicht als normaler Abschnitt durchgeht.
 */
const StatusNotice = () => (
  <section
    id="stand"
    data-testid="contact-status-section"
    className="relative isolate overflow-hidden -mt-6 lg:-mt-12 pt-16 pb-20 lg:pt-20 lg:pb-28"
  >
    <div className="px-6 md:px-10 lg:px-16">
      <Reveal className="relative">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -inset-x-6 -inset-y-10 rounded-[3rem] bg-gradient-to-br from-brand-orange/25 via-transparent to-brand-blue/25 blur-3xl animate-pulseGlow"
        />
        <Tape className="top-0 rotate-[3deg]" />

        <div className="relative z-10 rounded-[2rem] p-[1.5px] bg-gradient-to-br from-brand-orange via-white/10 to-brand-blue">
          <div className="relative overflow-hidden rounded-[calc(2rem-1.5px)] bg-navy-900 px-7 pb-9 pt-20 md:px-10 md:pb-12 md:pt-24 lg:px-14 lg:pb-14 lg:pt-28">
            <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-24 w-96 h-96 rounded-full bg-brand-orange/15 blur-[120px]" />
            <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-brand-blue/15 blur-[120px]" />

            <div className="relative grid lg:grid-cols-12 gap-12 lg:gap-16">
              <div className="lg:col-span-6 xl:col-span-5">
                <p className="sr-only">Stand {STATUS_UPDATED}</p>
                <h2 className="font-display font-black tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
                  MatchGate ist{" "}
                  <span className="gradient-text whitespace-nowrap">im Aufbau</span>.
                </h2>
                <p className="mt-6 text-base md:text-lg leading-relaxed text-white/60">
                  Konzept, Messlogik und Demo stehen, die Plattform selbst bauen
                  wir gerade, los geht es {STATUS_LAUNCH.in}. Registrieren
                  könnt ihr euch deshalb noch nicht, einen Platz auf der
                  Warteliste vormerken schon: Dann erfahrt ihr als Erste, wann
                  es losgeht.
                </p>
                <p className="mt-4 text-sm leading-relaxed text-white/45">
                  Wer bis zum {FOUNDER_UNTIL} abschließt, zahlt{" "}
                  {FOUNDER_MONTHS} Monate lang den{" "}
                  <Link
                    to="/preisstruktur"
                    className="text-white/70 underline decoration-white/25 underline-offset-4 hover:text-white hover:decoration-brand-orange transition-colors duration-300"
                  >
                    Gründerpreis
                  </Link>
                  .
                </p>

                <div className="mt-9 flex flex-col sm:flex-row gap-4">
                  <Link
                    to={ROUTE_WAITLIST}
                    data-testid="contact-status-waitlist"
                    className={BTN_PRIMARY}
                  >
                    Platz vormerken
                    <BellRing className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
                  </Link>
                  <Link to={ROUTE_DEMO} className={BTN_SECONDARY}>
                    Demo ansehen
                    <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-6 xl:col-span-6 xl:col-start-7 lg:pt-2">
                <span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-white/40 mb-7">
                  Der Weg zum Start
                </span>
                <StatusPhases />
              </div>
            </div>
          </div>
        </div>

        <Tape front className="top-0 -rotate-[3deg] z-20" />
      </Reveal>
    </div>
  </section>
);

// Beschriftungen für Fehlermeldungen des Skripts (public/api/kontakt.php)
const FIELD_LABELS = {
  topic: "Anliegen",
  role: "Ich schreibe als",
  name: "Name",
  organisation: "Verein / Unternehmen",
  email: "E-Mail",
  message: "Nachricht",
};

const EMPTY_FORM = {
  name: "",
  organisation: "",
  email: "",
  role: "verein",
  message: "",
};

const FORM_CARD =
  "relative overflow-hidden rounded-[2rem] border border-white/10 backdrop-blur-xl p-7 md:p-10";

const ContactForm = ({ topic, onTopicChange }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  // idle | sending | sent
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const guard = useSpamGuard();
  const sentRef = useRef(null);

  useEffect(() => {
    if (status === "sent") revealElement(sentRef.current);
  }, [status]);

  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const role = ROLES.find((r) => r.value === form.role) ?? ROLES[0];
  const current = TOPICS.find((t) => t.value === topic) ?? TOPICS[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setError("");
    const result = await postJson(API_CONTACT, {
      topic: current.value,
      ...form,
      ...guard.fields(),
    });
    if (result.ok) {
      setStatus("sent");
    } else {
      setError(formErrorMessage(result, FIELD_LABELS));
      setStatus("idle");
    }
  };

  const startOver = () => {
    setForm((prev) => ({ ...EMPTY_FORM, role: prev.role }));
    guard.reset();
    setStatus("idle");
  };

  if (status === "sent") {
    return (
      <div
        ref={sentRef}
        id="kontakt-gesendet"
        data-testid="contact-sent"
        className={`${FORM_CARD} scroll-mt-header`}
      >
        <div className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full bg-brand-orange/15 blur-[110px]" />
        <div className="relative">
          <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/[0.06] border border-white/10 mb-6">
            <CircleCheck className="w-5 h-5 text-brand-orange" />
          </span>
          <h3 className="font-display font-bold text-2xl md:text-3xl text-white">
            Danke, eure Nachricht ist angekommen.
          </h3>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-white/60">
            Wir lesen jede Nachricht selbst und antworten in der Regel
            innerhalb eines Werktages an{" "}
            <span className="text-white/85">{form.email}</span>.
          </p>
          <button
            type="button"
            onClick={startOver}
            className={`${BTN_SECONDARY} mt-8`}
          >
            Weitere Nachricht schreiben
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="contact-form" className={FORM_CARD}>
      <div className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full bg-brand-orange/15 blur-[110px]" />
      <Honeypot id="contact-website" {...guard.honeypot} />

      <div className="relative space-y-6">
        <ChipGroup
          label="Mein Anliegen"
          options={TOPICS}
          value={current.value}
          onChange={onTopicChange}
        />
        <ChipGroup
          label="Ich schreibe als"
          options={ROLES}
          value={form.role}
          onChange={(value) => setForm((prev) => ({ ...prev, role: value }))}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className={LABEL} htmlFor="contact-name">
              Name
            </label>
            <input
              id="contact-name"
              className={FIELD}
              type="text"
              required
              maxLength={200}
              autoComplete="name"
              placeholder="Vor- und Nachname"
              value={form.name}
              onChange={update("name")}
            />
          </div>
          <div>
            <label className={LABEL} htmlFor="contact-org">
              Verein / Unternehmen
            </label>
            <input
              id="contact-org"
              className={FIELD}
              type="text"
              maxLength={200}
              autoComplete="organization"
              placeholder={role.org}
              value={form.organisation}
              onChange={update("organisation")}
            />
          </div>
        </div>

        <div>
          <label className={LABEL} htmlFor="contact-email">
            E-Mail
          </label>
          <input
            id="contact-email"
            className={FIELD}
            type="email"
            required
            maxLength={254}
            autoComplete="email"
            placeholder={role.mail}
            value={form.email}
            onChange={update("email")}
          />
        </div>

        <div>
          <label className={LABEL} htmlFor="contact-message">
            Worum geht es?
          </label>
          <textarea
            id="contact-message"
            className={`${FIELD} min-h-[160px] resize-y leading-relaxed`}
            required
            maxLength={5000}
            placeholder={current.placeholder}
            value={form.message}
            onChange={update("message")}
          />
        </div>

        <FormError>{error}</FormError>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-1">
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={status === "sending"}
            className={`${BTN_PRIMARY} shrink-0 disabled:opacity-70 disabled:pointer-events-none`}
          >
            {status === "sending" ? "Wird gesendet …" : "Nachricht senden"}
            {status === "sending" ? (
              <LoaderCircle className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" />
            )}
          </button>
          <p className="text-xs leading-relaxed text-white/40">
            Eure Nachricht geht direkt an{" "}
            <span className="text-white/60">{CONTACT_EMAIL}</span>. Wie wir mit
            euren Angaben umgehen, steht in der{" "}
            <Link
              to="/datenschutz"
              className="text-white/60 underline decoration-white/25 underline-offset-4 hover:text-white transition-colors duration-300"
            >
              Datenschutzerklärung
            </Link>
            .
          </p>
        </div>
      </div>
    </form>
  );
};

export default function Contact() {
  const [topic, setTopic] = useState("frage");

  return (
    <PageLayout sidebarItems={SIDEBAR_ITEMS} title="Kontakt" testId="contact-main">
      <PageHero
        eyebrow="Kontakt"
        titleLines={["Ob Verein oder", "Unternehmen,"]}
        titleAccent="schreibt uns."
        lead="Wir sind ein kleines Team und lesen jede Nachricht selbst. Ob Frage, Kennenlernen oder Presseanfrage: Ein paar Sätze reichen, wir antworten in der Regel innerhalb eines Werktages."
        secondary={{ label: "Direkt zum Formular", href: "#formular", icon: ArrowDown }}
      />

      <StatusNotice />

      {/* Anliegen */}
      <section
        id="anliegen"
        data-testid="contact-topics-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Anliegen"
            title="Worüber ihr uns "
            accent="schreiben"
            tail=" könnt."
            lead="Ob ihr Verein oder Unternehmen seid, spielt dafür keine Rolle. Wählt ein Anliegen, das Formular stellt sich darauf ein."
          />

          <div className="grid gap-5 md:gap-6 lg:grid-cols-3">
            {CONCERNS.map((c, i) => {
              const Icon = c.icon;
              const label = TOPICS.find((t) => t.value === c.topic)?.label;
              return (
                <Reveal key={c.title} delay={i * 0.07}>
                  <div className="group relative h-full flex flex-col overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-8 hover:border-white/20 transition-colors duration-500">
                    <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 mb-6">
                      <Icon className="w-5 h-5 text-brand-orange" />
                    </span>
                    <span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-white/35 mb-3">
                      {label}
                    </span>
                    <h3 className="font-display font-bold text-xl text-white mb-3">
                      {c.title}
                    </h3>
                    <p className="text-base leading-relaxed text-white/55">
                      {c.text}
                    </p>
                    <div className="mt-auto pt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
                      <a
                        href="#formular"
                        onClick={() => setTopic(c.topic)}
                        className="group/link inline-flex items-center gap-2 text-sm font-bold text-white"
                      >
                        {c.action}
                        <ArrowDown className="w-4 h-4 group-hover/link:translate-y-0.5 transition-transform duration-300" />
                      </a>
                      {c.link && (
                        <Link
                          to={c.link.to}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/50 hover:text-white transition-colors duration-300"
                        >
                          {c.link.label}
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                      )}
                    </div>
                    <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-brand-orange to-brand-blue group-hover:w-full transition-all duration-700" />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Formular */}
      <section
        id="formular"
        data-testid="contact-form-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-0 left-1/4 w-[560px] h-[420px] rounded-full bg-brand-blue/10 blur-[170px]" />
        </div>

        <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-sticky">
              <SectionHeading
                eyebrow="Nachricht"
                title="Sagt uns kurz, wer ihr "
                accent="seid"
                tail="."
                lead="Je konkreter eure Situation, desto konkreter unsere Antwort. Ein paar Sätze reichen völlig."
                className="max-w-md mb-8"
              />

              <Reveal delay={0.1}>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 rounded-2xl border border-white/10 backdrop-blur-xl px-5 py-4">
                    <span className="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                      <Mail className="w-[18px] h-[18px] text-brand-orange" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-white/40">
                        E-Mail
                      </div>
                      <a
                        href={`mailto:${CONTACT_EMAIL}`}
                        className="text-sm font-semibold text-white hover:text-brand-blue transition-colors duration-300"
                      >
                        {CONTACT_EMAIL}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 rounded-2xl border border-white/10 backdrop-blur-xl px-5 py-4">
                    <span className="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                      <Phone className="w-[18px] h-[18px] text-brand-blue" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-white/40">
                        Telefon
                      </div>
                      <a
                        href={CONTACT_PHONE_HREF}
                        className="text-sm font-semibold text-white hover:text-brand-blue transition-colors duration-300"
                      >
                        {CONTACT_PHONE}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 rounded-2xl border border-white/10 backdrop-blur-xl px-5 py-4">
                    <span className="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                      <Clock3 className="w-[18px] h-[18px] text-brand-orange" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-white/40">
                        Antwortzeit
                      </div>
                      <div className="text-sm font-semibold text-white">
                        In der Regel ein Werktag
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.05}>
              <ContactForm topic={topic} onTopicChange={setTopic} />
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand
        title="Lieber einmal "
        accent="anschauen"
        tail="?"
        lead="Stellt in der Demo ein Beispiel-Sponsoring zusammen und seht, was daraus entsteht. Gern stellen wir euch MatchGate auch persönlich vor."
        primary={{ label: "Demo ansehen", to: ROUTE_DEMO }}
        secondary={{ label: "Zur Startseite", to: "/" }}
      />
    </PageLayout>
  );
}
